import type { CertificationProfileAttributes } from '@/server/models'

import { CertificationRequestType, TopicType } from '@/queries/constants'
import { SQL, type SQLFragment } from '@/client/query'

import CertificationRequestQ from './CertificationRequestQ'
import CourseQ from './CourseQ'
import {
  type GroupBy,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import TopicQ from './TopicQ'
import UserQ from './UserQ'

const TranscriptCourseTopic = SQL`"topic__certification_profile_transcript"`

const CertificationProfileQ = {
  ...makeQueryModelV2<CertificationProfileAttributes>('certification_profile')([
    'id',
    'user_id',
    'first_name',
    'last_name',
    'college_search_opt_in',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),
  where: (
    {
      paranoid = true,
      userId,
      groupBy = null,
      order = null,
    }: {
      paranoid?: boolean
      userId?: number | null | SQLFragment<number>
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<CertificationProfileAttributes> => SQL`
        FROM ${CertificationProfileQ}
        WHERE
            ${userId != null ? SQL`${CertificationProfileQ.user_id} = ${userId} AND` : SQL``}
            ${paranoid ? SQL`${CertificationProfileQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getTranscriptFromCertificationProfile: (uuidTranscript: string): SQLFragment<unknown> => SQL`
        FROM (
            SELECT row_to_json(grp.*) as result FROM (
                SELECT
                    json_agg(
                        json_build_object(
                            'topic_id', ${TopicQ.id},
                            'topic_name', ${TopicQ.name},
                            'topic_type', ${TopicQ.type},
                            'topic_active', ${TopicQ.active},
                            'recording_url', ${CertificationRequestQ.recording_url},
                            'created_at', ${CertificationRequestQ.created_at},
                            'approved_at', ${CertificationRequestQ.approved_at}
                        )
                        ORDER BY ${TopicQ.weight} DESC
                    ) as topics,
                    ${CourseQ.name} AS course_name,
                    ${CourseQ.weight} AS course_weight,
                    ${CourseQ.icon} AS course_icon
                FROM
                    ${TopicQ}
                    INNER JOIN ${CourseQ} ON ${TopicQ.course_id} = ${CourseQ.id}
                    LEFT JOIN ${CertificationRequestQ}
                        ON ${CertificationRequestQ.topic_id} = ${TopicQ.id} AND
                        ${CertificationRequestQ.user_id} = (
                            SELECT ${UserQ.id} ${UserQ.where({ uuidTranscript })}
                        )
                        AND ${CertificationRequestQ.approved_at} IS NOT NULL
                        AND ${CertificationRequestQ.type} != ${CertificationRequestType.Honorary}
                WHERE
                    ${TopicQ.course_id} IN (
                        SELECT
                            ${TranscriptCourseTopic}."course_id" FROM ${TopicQ} ${TranscriptCourseTopic}
                        WHERE
                            ${TranscriptCourseTopic}."id" IN(
                                SELECT
                                    ${CertificationRequestQ.topic_id} FROM ${CertificationRequestQ}
                                WHERE
                                    ${CertificationRequestQ.user_id} = (
                                        SELECT ${UserQ.id} ${UserQ.where({
                                          uuidTranscript,
                                        })}
                                    )
                                AND ${CertificationRequestQ.approved_at} IS NOT NULL
                                AND ${CertificationRequestQ.type} != ${
                                  CertificationRequestType.Honorary
                                }
                            )
                    )
                    AND ${TopicQ.type} != ${TopicType.qa}
                    AND ${TopicQ.type} != ${TopicType.ap}
                GROUP BY
                    "course_name", "course_weight", "course_icon"
            ) as grp
            ORDER BY "course_weight" DESC
        ) as relevant_courses
    `,
}

export default CertificationProfileQ
